import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Button,
  FormControl,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../../api/hooks/useColors";
import { controlKeys } from "../../../../../api/queryKeys";
import ContainedButton from "../../../../../components/buttons/ContainedButton";
import TextButton from "../../../../../components/buttons/TextButton";
import CustomToolTipWithPermission from "../../../../../components/CustomToolTipWithPermission";
import ConfirmationDialog from "../../../../../components/dialogs/ConfirmationDialog";
import { StyledTextField } from "../../../../../components/forms/styles/formFieldStyles";
import { Toast } from "../../../../../components/Toast";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { PLATFORM_USER_ACTIONS } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import { DURATION_TYPES, SnoozeType } from "./constants";
import DurationSelector from "./DurationSelector";
import { getFinalPeriodDays } from "./utils";

export default function SnoozeButton({
  key = null,
  controlIds = [],
  initialIsSnooze,
  misconfigIds = [],
  snoozeType,
  showUnsnoozeButton = true,
  fromDrawer = false,
  hideTitle = false,
  tooltipTitle = null,
  ...props
}) {
  const colors = useColors();
  const [isSnoozed, setIsSnoozed] = useState(initialIsSnooze);

  const customReasonOption = "Custom...";
  const snoozeReasons = [
    "Incorrect Data",
    "Risk Accepted",
    "Not Relevant",
    customReasonOption,
  ];
  const [anchorEl, setAnchorEl] = useState(null);
  const { user } = useAuth0();
  const {
    trackEvent,
    snoozeControl,
    unsnoozeControl,
    snoozeMisconfiguration,
    unsnoozeMisconfiguration,
  } = useAPI();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState(0);
  const [durationMode, setDurationMode] = useState({
    type: DURATION_TYPES.PRESET,
    value: 0,
    selectedDate: null,
  });
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const snoozeReasonMaxLength = 100;

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.SNOOZE_AND_UNSNOOZE
  );

  const isMultiple = misconfigIds?.length > 1 || controlIds?.length > 1;

  const handleSnooze = (periodDays, reason) => {
    setAnchorEl(null);
    let finalReason = reason === customReasonOption ? customReason : reason;

    if (!finalReason || finalReason.trim().length === 0) {
      Toast.error("Please provide a valid reason.");
      return;
    }

    const ids = snoozeType === SnoozeType.CONTROL ? controlIds : misconfigIds;
    const targetLabel =
      snoozeType === SnoozeType.CONTROL ? "control" : "misconfiguration";
    const label = isMultiple ? `${pluralize(targetLabel)}` : targetLabel;
    const message = `User pressed on "snooze" for ${label}: ${
      isMultiple ? ids.join(", ") : ids[0]
    } for ${periodDays} days with reason: ${finalReason}`;
    trackEvent(message).then();

    const apiCall =
      snoozeType === SnoozeType.CONTROL
        ? () =>
            snoozeControl(
              controlIds,
              user.email,
              user.name,
              periodDays,
              finalReason
            )
        : () =>
            snoozeMisconfiguration(
              misconfigIds,
              user.email,
              user.name,
              periodDays,
              finalReason
            );

    apiCall()
      .then(() => {
        queryClient.invalidateQueries({ queryKey: controlKeys.all });
        Toast.success(
          `Risk temporarily accepted. Breeze analysis paused for ${periodDays} days.`
        );
        if (snoozeType === SnoozeType.CONTROL) {
          setIsSnoozed(true);
        }
      })
      .catch(() => {
        Toast.error("Risk acceptance failed, please try again later.");
        if (snoozeType === SnoozeType.CONTROL) {
          setIsSnoozed(false);
        }
      });

    setIsDialogOpen(false);
    setSelectedDays(0);
    setSelectedReason("");
    setCustomReason("");
  };

  const handleUnsnooze = () => {
    const ids = snoozeType === SnoozeType.CONTROL ? controlIds : misconfigIds;
    const targetLabel =
      snoozeType === SnoozeType.CONTROL ? "control" : "misconfiguration";
    const label = isMultiple ? `${pluralize(targetLabel)}` : targetLabel;
    const message = `User pressed on "Unsnooze" for ${label}: ${
      isMultiple ? ids.join(", ") : ids[0]
    }`;
    trackEvent(message).then();

    const apiCall =
      snoozeType === SnoozeType.CONTROL
        ? () => unsnoozeControl(controlIds, user.email)
        : () => unsnoozeMisconfiguration(misconfigIds, user.email);

    apiCall()
      .then(() => {
        queryClient.invalidateQueries({ queryKey: controlKeys.all });
        Toast.success(
          "Breeze analysis resumed. Monitoring this gap is active again."
        );

        if (snoozeType === SnoozeType.CONTROL) {
          setIsSnoozed(false);
        }
      })
      .catch(() => {
        Toast.error("Risk acceptance failed, please try again later.");
        if (snoozeType === SnoozeType.CONTROL) {
          setIsSnoozed(true);
        }
      });
  };

  const handleClick = (event) => {
    trackEvent(
      "User pressed on Snooze button for " +
        (snoozeType === SnoozeType.CONTROL ? "controls" : "misconfigurations")
    ).then();

    if (isSnoozed && showUnsnoozeButton) {
      handleUnsnooze();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSnoozeItemClick = () => {
    const periodDays = getFinalPeriodDays(durationMode);
    handleSnooze(periodDays, selectedReason);
  };

  const handleClose = () => {
    trackEvent("User closed Snooze menu");
    setAnchorEl(null);
    setSelectedDays(0);
    setSelectedReason("");
    setCustomReason("");
  };

  const handleMenuItemClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    trackEvent("User closed Snooze dialog").then();
    setIsDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    trackEvent("User pressed on Accept button in Snooze dialog").then();
    handleSnoozeItemClick();
  };

  const SelectChevronIcon = () => (
    <SvgToIcon
      IconLogo={icons.chevronDownIcon}
      color={colors.icon_action_active}
      width="20px"
    />
  );

  const hasCheckedItems =
    (controlIds?.length || 0) > 0 || (misconfigIds?.length || 0) > 0;

  const getIsDisabled = () => {
    const externallyDisabled = Boolean(props?.disabled);
    return externallyDisabled || !hasPermission || !hasCheckedItems;
  };

  const getTooltipTitle = () => {
    if (!hasCheckedItems) {
      if (isSnoozed && showUnsnoozeButton) {
        return `Please select a ${
          snoozeType === SnoozeType.CONTROL ? "control" : "misconfiguration"
        } to resume Breeze analysis for`;
      } else {
        return snoozeType === SnoozeType.CONTROL
          ? "Please select a control to snooze"
          : "Please select a misconfiguration to snooze";
      }
    }
    if (isSnoozed && showUnsnoozeButton) {
      if (isMultiple)
        return `Resume Breeze analysis of these ${
          snoozeType === SnoozeType.CONTROL ? "controls" : "misconfigurations"
        }`;
      else
        return `Resume Breeze analysis of this ${
          snoozeType === SnoozeType.CONTROL ? "gap" : "misconfiguration"
        }`;
    }

    if (!isSnoozed)
      return "Temporarily accept the risk for a specified duration";
  };

  const getDialogContentFirstLine = () => {
    if (snoozeType === SnoozeType.MISCONFIGURATION) {
      return `By snoozing ${
        isMultiple ? "these misconfigurations" : "this misconfiguration"
      }, you accept the risk and temporarily pause Breeze analysis across ${
        isMultiple ? "theirs" : "its"
      } affected entities.`;
    }
    if (snoozeType === SnoozeType.AFFECTED_ENTITY) {
      return `By snoozing ${
        isMultiple ? "these entities" : "this entity"
      }, you accept the risk and temporarily pause Breeze analysis for ${
        isMultiple ? "these entities" : "this entity"
      } in this risk assessment.`;
    }
    return `By snoozing ${
      isMultiple ? "these gaps" : "this gap"
    }, you accept the risk and temporarily pause Breeze analysis of related scenarios.`;
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={tooltipTitle ?? getTooltipTitle()}
        permission={hasPermission}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              backgroundColor: colors.common_white,
              color: colors.text_primary,
              minWidth: "auto",
              width: "32px",
              height: "32px",
              border: "1px solid " + colors.enabled_stroke,
              borderRadius: "8px",
            }}
            onClick={handleClick}
            {...props}
            // getIsDisabled() takes precedence over parent props.disabled
            disabled={getIsDisabled()}
          >
            {isSnoozed && showUnsnoozeButton ? (
              <SvgToIcon
                IconLogo={icons.UnsnoozedIcon}
                style={{
                  width: "18px",
                  color: getIsDisabled()
                    ? colors.action_disabled
                    : colors.icon_action_active,
                }}
              />
            ) : (
              <SvgToIcon
                IconLogo={icons.SnoozedIcon}
                style={{
                  width: "18px",
                  color: getIsDisabled()
                    ? colors.action_disabled
                    : colors.icon_action_active,
                }}
              />
            )}
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: "240px",
              borderRadius: "10px",
              boxShadow: `0px 2px 4px 0px ${colors.cardEntityShadow}`,
              padding: "8px 16px",
              backgroundColor: colors.modal_bg,
            },
          },
        }}
      >
        <DurationSelector
          durationMode={durationMode}
          onDurationChange={setDurationMode}
          selectedDays={selectedDays}
          onSelectedDaysChange={(v) => setSelectedDays(v)}
        />
        <Typography
          variant="overline"
          sx={{
            marginBottom: "10px",
            color: colors.text_primary,
          }}
        >
          Why are you snoozing?
        </Typography>
        <FormControl fullWidth sx={{ marginBottom: "10px" }}>
          <Select
            value={selectedReason}
            displayEmpty
            onChange={(e) => setSelectedReason(e.target.value)}
            IconComponent={SelectChevronIcon}
            renderValue={(selected) => {
              if (selected === "") {
                return (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.icon_action_active }}
                  >
                    Select a reason...
                  </Typography>
                );
              }
              return (
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_primary }}
                >
                  {selected}
                </Typography>
              );
            }}
            sx={{
              borderRadius: "10px",
              border: `1px solid ${colors.grey_20percent}`,
              height: "32px",
              marginTop: "8px",
              paddingRight: "6px",
              "& .MuiSelect-select": {
                padding: "8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-root": {
                boxShadow: "none",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "10px",
                },
              },
            }}
          >
            {snoozeReasons.map((reason) => (
              <MenuItem
                key={reason}
                value={reason}
                sx={{ borderRadius: "10px", margin: "8px", padding: "8px" }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_primary }}
                >
                  {reason}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedReason === customReasonOption && (
          <>
            <StyledTextField
              label={
                <Typography variant="caption">Enter your reason</Typography>
              }
              InputLabelProps={{
                shrink: true,
                sx: { color: colors.text_primary },
              }}
              fullWidth
              multiline
              autoFocus
              rows={4}
              variant="outlined"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              inputProps={{ maxLength: snoozeReasonMaxLength }}
              sx={{
                borderRadius: "10px",
                width: "100%",
                marginTop: "8px",
                "& .MuiOutlinedInput-root": {
                  padding: "16px  14px",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${colors.grey_20percent}`,
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: `1px solid ${colors.grey_20percent}`,
                  },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                  color: colors.text_primary,
                  lineHeight: 1.5,
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                },
              }}
              placeholder="Reason for excluding is...."
              helperText={
                <Typography
                  variant="caption"
                  sx={{ color: colors.helper_text }}
                >
                  {customReason.length}/{snoozeReasonMaxLength}
                </Typography>
              }
            />
          </>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: "12px" }}
        >
          <TextButton onClick={handleClose}>
            <Typography variant="overline">Cancel</Typography>
          </TextButton>

          <ContainedButton
            color="primary"
            size="small"
            disabled={
              !getFinalPeriodDays(durationMode) ||
              !selectedReason ||
              (selectedReason === customReasonOption && !customReason.trim())
            }
            onClick={handleMenuItemClick}
          >
            <Typography variant="overline">Confirm</Typography>
          </ContainedButton>
        </Box>
      </Menu>
      <ConfirmationDialog
        openDialog={isDialogOpen}
        handleDialogClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
        dialogTitle={<Typography variant="subtitle1">Confirmation</Typography>}
        dialogContentFirstLine={getDialogContentFirstLine()}
        confirmationButtonText={"Accept the risk"}
        cancelButtonText={"Cancel"}
        sx={{
          "& .MuiButton-text": {
            color: colors.button_text_primary_enabled_text,
          },
        }}
        showCloseButton={true}
      />
    </>
  );
}
